import React, { useCallback, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeContext } from 'styled-components';
import { SonntagIcon } from '@getgrover/ui';
import { openHelpCenter } from '@devsbb/cs-sdk';

import formatTime from 'ggApp/utils/time/formatTime';
import { accountOutlineIconPath, phoneIconPath } from 'ggApp/shared/icons/IconPaths';
import Card, { SubscriptionAction as Action } from 'ggApp/shared/common/Subscription/Actions';
import PureTranslator from 'ggApp/modules/translator/components/PureTranslator';

import { ProgressRingIcon } from './styles';

type ResendCodeActionsProps = {
    resendCodeAvailableTime: number;
    setResendCodeAvailableTime: (resendCodeAvailableTime: number) => void;
    defaultTimeoutInSeconds: number;
    onSubmit: () => void;
};

const oneSecondInterval = 1000;
const defaultAnimationDuration = 300;

const ResendCodeActions = ({
    resendCodeAvailableTime,
    setResendCodeAvailableTime,
    defaultTimeoutInSeconds = 0,
    onSubmit,
}: ResendCodeActionsProps): JSX.Element => {
    const theme = useContext(ThemeContext);
    const [countdown, setCountdown] = useState(0);
    const [animationDuration, setAnimationDuration] = useState(defaultAnimationDuration);

    useEffect(() => {
        let timeoutId = 0;
        let intervalId = 0;

        if (resendCodeAvailableTime > Date.now()) {
            const timeoutInMs = resendCodeAvailableTime - Date.now();
            setCountdown(timeoutInMs);
            setAnimationDuration(timeoutInMs);

            intervalId = window.setInterval(() => {
                setCountdown((milliseconds) => milliseconds - oneSecondInterval);
            }, oneSecondInterval);

            timeoutId = window.setTimeout(() => {
                window.clearTimeout(timeoutId);
                window.clearInterval(intervalId);
                setCountdown(0);
                setAnimationDuration(defaultAnimationDuration);
            }, timeoutInMs);
        }

        return (): void => {
            window.clearTimeout(timeoutId);
            window.clearInterval(intervalId);
        };
    }, [defaultTimeoutInSeconds, resendCodeAvailableTime]);

    const formattedCountdown = useMemo(
        () =>
            formatTime({
                date: new Date(countdown),
                locale: 'en-GB',
                formatOptions: {
                    timeZone: 'UTC',
                    hour: 'numeric',
                    minute: 'numeric',
                    second: 'numeric',
                },
            }),
        [countdown],
    );

    const resendCode = useCallback(() => {
        setResendCodeAvailableTime(Date.now() + defaultTimeoutInSeconds * 1000);
        onSubmit();
    }, [onSubmit, defaultTimeoutInSeconds, setResendCodeAvailableTime]);

    const hasCountdown = countdown > 0;

    return (
        <Card onContactButtonClick={(): void => {}}>
            <Action
                disabled={hasCountdown}
                title={<PureTranslator tk="TWO_FACTOR_AUTH_RESEND_CODE_ACTION_TITLE" />}
                description={
                    <PureTranslator
                        tk={
                            hasCountdown
                                ? 'TWO_FACTOR_AUTH_RESEND_CODE_ACTION_DESCRIPTION_DISABLED'
                                : 'TWO_FACTOR_AUTH_RESEND_CODE_ACTION_DESCRIPTION'
                        }
                        withValues={{ timeout: formattedCountdown }}
                    />
                }
                icon={
                    <React.Fragment>
                        <SonntagIcon iconPath={phoneIconPath} color={theme.colors.secondary} />
                        <ProgressRingIcon
                            diameter={48}
                            stroke={2}
                            progress={hasCountdown ? 100 : 0}
                            color={theme.colors.secondary}
                            animationDuration={animationDuration}
                        />
                    </React.Fragment>
                }
                color={theme.colors.secondaryBackground}
                onClick={resendCode}
            />
            <Action
                onClick={openHelpCenter}
                target="_blank"
                title={<PureTranslator tk="TWO_FACTOR_AUTH_CONTACT_US_ACTION_TITLE" />}
                description={<PureTranslator tk="TWO_FACTOR_AUTH_CONTACT_US_ACTION_DESCRIPTION" />}
                icon={
                    <SonntagIcon iconPath={accountOutlineIconPath} color={theme.colors.secondary} />
                }
                color={theme.colors.secondaryBackground}
            />
        </Card>
    );
};

export default ResendCodeActions;
